import React, {useState, useRef, useContext, useEffect} from "react"
import {DataTable} from "primereact/datatable"
import {Column} from "primereact/column"
// import {Skeleton} from "primereact/skeleton"
// import useSWR from "swr"

import {Main, FiltersTable} from "../../components/Layout"
import {bulkFilter} from "../../utilities/general"
import SubHeader from "../../components/SubHeader"
import CenterLoader from "../../components/CenterLoader"

import {getCellCustom, getExportableField} from "../../utilities/admin"
import {wrappedDelete, wrappedGet} from "../../utilities/AxiosHelper"
import RefContext from "../../utilities/RefContext"
import {useStableNavigate} from "../../utilities/StableNavigateContext"
import AddOrganizationModal from "../../components/AddOrganizationModal"

/**
 * OrganizationList Component
 * Renders the organization list view
 */
const OrganizationList = () => {
    const [searchText, setSearchText] = useState("")
    const [showModal, setShowModal] = useState(false)
    const tableRef = useRef(null)
    const {dispatch} = useContext(RefContext)
    const navigate = useStableNavigate()
    const [orgs, setOrgs] = useState([])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        document.title = `Organizations`
        fetchOrgs()
    }, [])

    const fetchOrgs = () => {
        return wrappedGet(`get-organizations`)
            .then((response) => {
                const data = response.data
                if (!data) return

                setOrgs(data.results)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Organization",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    let filteredOrgs = bulkFilter(orgs, [[["name"], searchText, "inexact"]])

    const addOrganization = () => {
        setShowModal(true)
    }

    const hideModal = () => {
        setShowModal(false)
        setIsLoading(true)
        fetchOrgs()
    }

    const handleReset = () => {
        setSearchText("")
    }

    const handleExport = () => {
        tableRef.current.exportCSV()
    }

    const columns = [
        {header: "Name", field: "name", export: true},
        {header: "Short Name", field: "shortname", export: true},
        {header: "Customer Key", field: "customer_key", export: true},
        {header: "Active", field: "active", export: true},
        {header: "Created At", field: "created_at", export: true},
        {header: "Updated At", field: "updated_at", export: true},
        {header: "Actions", field: "actions", export: false},
    ]

    const editOrg = (selectedRow) => {
        navigate(`/admin/organization/${selectedRow.id}`)
    }

    const deleteRecord = (org) => {
        let confirmAction = window.confirm("Are you sure you want to disable this organization?")
        if (confirmAction) {
            wrappedDelete(`organization/${org.id}`)
                .then((response) => {
                    if (response.status === 200) {
                        dispatch("showToast", {
                            show: true,
                            variant: "success",
                            text: "Organization is disabled",
                            timeout: 5000,
                        })
                    } else {
                        dispatch("showToast", {
                            show: true,
                            variant: "danger",
                            text: "Error disabling organization",
                            timeout: 5000,
                        })
                        return
                    }
                })
                .catch((error) => {
                    dispatch("showToast", {
                        show: true,
                        variant: "danger",
                        text: "Error disabling organization",
                        timeout: 5000,
                    })
                    return
                })
        }
    }

    if (isLoading) {
        return <CenterLoader />
    }

    return (
        <>
            <SubHeader title1="OARS" title2="Organizations" icon="fa-users-cog">
                <div className="d-flex flex-grow-1 justify-content-end">
                    <button type="button" className="btn btn-light-primary me-3" onClick={() => handleExport()}>
                        <i className="ki-outline ki-exit-up fs-2"></i>Export
                    </button>
                    <button type="button" className="btn btn-primary fw-semibold" onClick={() => addOrganization()}>
                        <i className="fas fa-plus"></i>Organization
                    </button>
                </div>
            </SubHeader>
            <FiltersTable
                Filters={
                    <div className="row g-8 align-items-end">
                        <div className="col-lg-2 col-md-12">
                            <label htmlFor="id-search-organizations" className="form-label fw-bold">
                                Search Orgs:
                            </label>
                            <div className="d-flex align-items-center position-relative">
                                <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                                <input
                                    id="id-search-organizations"
                                    type="text"
                                    className="form-control form-control-solid ps-12"
                                    placeholder="Name"
                                    value={searchText}
                                    onChange={(evt) => {
                                        setSearchText(evt.target.value)
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-lg-2 col-md-6">
                            <div className="d-flex justify-content-start">
                                <button
                                    type="reset"
                                    className="btn btn-secondary fw-semibold me-2 px-6"
                                    onClick={handleReset}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                }
                Table={
                    <DataTable
                        ref={tableRef}
                        value={filteredOrgs}
                        tableClassName="table gy-3 gs-5 rounded w-100 mt-3 align-middle"
                        paginator
                        rows={25}
                        sortField={columns[0].field}
                        sortOrder={1}
                        rowsPerPageOptions={[25, 50, 75, 100]}
                        rowHover={true}
                        onMouseDownCapture={(e) => {
                            e.stopPropagation()
                        }}
                    >
                        {columns.map((colDef, index) => (
                            <Column
                                key={index}
                                field={colDef.field}
                                header={colDef.header}
                                headerClassName="th-sorting-center align-middle text-start text-light text-uppercase bg-gray-700 fs-7 fw-bold p-sortable-column"
                                sortable
                                body={(row) => getCellCustom(colDef, row, editOrg, deleteRecord, false)}
                                exportable={colDef.export}
                                exportField={(row) => getExportableField(colDef, row)}
                            />
                        ))}
                    </DataTable>
                }
            />
            <Main>
                {/* TODO: {!!org === true ? <EditOrganization organization={org} setOrg={setOrg} callMutate={mutate} /> : null} */}
            </Main>
            <AddOrganizationModal showModal={showModal} closeModal={hideModal} />
        </>
    )
}

export default OrganizationList
